import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Project } from '../../../../types/project';
import { ProjectService, ProjectImageStatus } from '../../../../services/projectService';
import { useTranslation } from 'react-i18next';
import { buildShareUrl } from '../../../../atoms/shared/regionAtom';

interface ProjectShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
}

export const ProjectShareModal: React.FC<ProjectShareModalProps> = ({
  isOpen,
  onClose,
  project,
}) => {
  const { t } = useTranslation(['v3/projects', 'common']);
  const inputRef = useRef<HTMLInputElement>(null);

  // 状态
  const [isPublic, setIsPublic] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // 图片相关状态
  const [imageStatus, setImageStatus] = useState<ProjectImageStatus>('not-started');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // 当弹窗打开时，检查项目是否已经公开分享
  useEffect(() => {
    if (isOpen && project) {
      setIsPublic(!!project.isPublic && !!project.shareId);
      if (project.shareId) {
        // 使用域名工具函数生成分享链接
        const url = buildShareUrl(`/s/project/${project.shareId}`);
        setShareUrl(url);
      } else {
        setShareUrl('');
      }
      setError(null);
      setCopied(false);

      // 检查图片状态
      const status = ProjectService.getImageStatus(project);
      setImageStatus(status);
      setImageUrl(project.ogImageUrl || null);
      setImageError(null);
    }
  }, [isOpen, project]);

  // 处理分享状态切换
  const handleShareToggle = async () => {
    setLoading(true);
    setError(null);

    try {
      let updatedProject;

      if (!isPublic) {
        // 分享项目
        updatedProject = await ProjectService.shareProject(project.id);
        showToast(t('v3/projects:share.sharedTitle'), t('v3/projects:share.sharedMessage'));
      } else {
        // 取消分享
        updatedProject = await ProjectService.unshareProject(project.id);
        showToast(t('v3/projects:share.unsharedTitle'), t('v3/projects:share.unsharedMessage'));
      }

      setIsPublic(!!updatedProject.isPublic && !!updatedProject.shareId);

      if (updatedProject.shareId) {
        // 使用域名工具函数生成分享链接
        const url = buildShareUrl(`/s/project/${updatedProject.shareId}`);
        setShareUrl(url);

        // 如果是新分享的项目，开始轮询图片状态
        if (!isPublic) {
          startImagePolling(updatedProject);
        }
      } else {
        setShareUrl('');
        // 取消分享时重置图片状态
        setImageStatus('not-started');
        setImageUrl(null);
        setImageError(null);
      }
    } catch (err) {
      console.error('Error toggling project share:', err);
      setError(err instanceof Error ? err.message : t('v3/projects:share.error'));
    } finally {
      setLoading(false);
    }
  };

  // 开始图片轮询
  const startImagePolling = async (projectData: Project) => {
    setImageLoading(true);
    setImageStatus('generating');
    setImageError(null);

    try {
      const result = await ProjectService.pollImageStatus(projectData.id);

      if (result.status === 'ready' && result.imageUrl) {
        setImageStatus('ready');
        setImageUrl(result.imageUrl);
        showToast(t('v3/projects:share.image.generationComplete'), t('v3/projects:share.image.generationCompleteDesc'));
      } else {
        setImageStatus('error');
        setImageError(result.error || t('v3/projects:share.image.generationFailed'));
      }
    } catch (error) {
      console.error('图片轮询失败:', error);
      setImageStatus('error');
      setImageError('检查图片状态失败');
    } finally {
      setImageLoading(false);
    }
  };

  // 下载项目图片
  const handleDownloadImage = async () => {
    if (!imageUrl || imageStatus !== 'ready') return;

    setDownloadLoading(true);
    try {
      await ProjectService.downloadProjectImage(project);
      showToast(t('v3/projects:share.image.downloadSuccess'), t('v3/projects:share.image.downloadSuccessDesc'));
    } catch (error) {
      console.error('下载图片失败:', error);
      showToast(t('v3/projects:share.image.downloadFailed'), error instanceof Error ? error.message : t('v3/projects:share.image.downloadFailed'));
    } finally {
      setDownloadLoading(false);
    }
  };

  // 显示Toast通知
  const showToast = (title: string, message: string) => {
    // 在iOS风格中，我们可以使用自定义Toast组件
    // 这里简化处理，仅打印到控制台
    console.log(`[Toast] ${title}: ${message}`);
  };

  // 复制分享链接
  const handleCopyLink = () => {
    if (!shareUrl) return;

    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setCopied(true);
        showToast(t('v3/projects:share.linkCopiedTitle'), '');

        // 3秒后重置复制状态
        setTimeout(() => {
          setCopied(false);
        }, 3000);
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        showToast(t('v3/projects:share.copyFailedTitle'), t('v3/projects:share.copyFailedMessage'));
      });
  };

  // 选择输入框内容
  const handleInputClick = () => {
    inputRef.current?.select();
  };

  // 处理背景点击
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 dark:bg-black/50"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-md mx-4 bg-white dark:bg-ios-dark-gray6 rounded-ios-lg shadow-ios dark:shadow-ios-dark overflow-hidden"
          >
            {/* 模态框标题 */}
            <div className="px-4 py-3 border-b border-ios-gray5 dark:border-ios-dark-gray4">
              <h2 className="text-ios-title font-semibold text-center">
                {t('v3/projects:share.title')}
              </h2>
            </div>

            {/* 模态框内容 */}
            <div className="p-4">
              {/* 错误提示 */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-ios-md text-ios-subhead">
                  {error}
                </div>
              )}

              {/* 分享开关 */}
              <div className="flex items-center justify-between py-3 border-b border-ios-gray5 dark:border-ios-dark-gray4">
                <div>
                  <p className="text-ios-body font-medium">{t('v3/projects:share.makePublic')}</p>
                  <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray mt-0.5">
                    {t('v3/projects:share.makePublicDescription')}
                  </p>
                </div>
                <button
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    isPublic
                      ? 'bg-ios-green dark:bg-ios-dark-green'
                      : 'bg-ios-gray4 dark:bg-ios-dark-gray3'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={handleShareToggle}
                  disabled={loading}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-ios transition-transform ${
                      isPublic ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* 分享链接 */}
              {isPublic && (
                <div className="mt-4">
                  <p className="text-ios-subhead font-medium mb-2">{t('v3/projects:share.link')}</p>
                  <div className="relative">
                    <input
                      ref={inputRef}
                      type="text"
                      value={shareUrl}
                      readOnly
                      onClick={handleInputClick}
                      className="w-full h-10 px-3 pr-20 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-ios-md text-ios-body focus:outline-none"
                    />
                    <button
                      onClick={handleCopyLink}
                      disabled={loading}
                      className={`absolute right-1 top-1 px-3 py-1.5 rounded-ios-md text-ios-footnote font-medium ${
                        copied
                          ? 'bg-ios-green dark:bg-ios-dark-green text-white'
                          : 'bg-ios-blue dark:bg-ios-dark-blue text-white'
                      }`}
                    >
                      {copied ? t('v3/projects:share.copied') : t('v3/projects:share.copyLink')}
                    </button>
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-ios-md flex">
                    <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue flex-shrink-0 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                      {t('v3/projects:share.linkDescription')}
                    </p>
                  </div>

                  {/* 项目图片预览区域 */}
                  <div className="mt-6">
                    <p className="text-ios-subhead font-medium mb-3">{t('v3/projects:share.image.title')}</p>

                    {/* 图片状态：生成中 */}
                    {imageStatus === 'generating' && (
                      <div className="flex items-center justify-center p-6 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-ios-lg">
                        <div className="text-center">
                          <div className="w-8 h-8 border-2 border-ios-blue/30 dark:border-ios-dark-blue/30 border-t-ios-blue dark:border-t-ios-dark-blue rounded-full animate-spin mx-auto mb-3"></div>
                          <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                            {t('v3/projects:share.image.generating')}
                          </p>
                          <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray mt-1">
                            {t('v3/projects:share.image.generatingDesc')}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* 图片状态：已生成 */}
                    {imageStatus === 'ready' && imageUrl && (
                      <div className="space-y-3">
                        <div className="relative rounded-ios-lg overflow-hidden bg-ios-gray5 dark:bg-ios-dark-gray4">
                          <img
                            src={imageUrl}
                            alt={t('v3/projects:share.image.alt')}
                            className="w-full h-auto"
                            onError={() => {
                              setImageStatus('error');
                              setImageError(t('v3/projects:share.image.loadFailed'));
                            }}
                          />
                        </div>
                        <button
                          onClick={handleDownloadImage}
                          disabled={downloadLoading}
                          className="w-full py-3 px-4 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-lg font-medium transition-colors hover:bg-ios-blue/90 dark:hover:bg-ios-dark-blue/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          {downloadLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                              {t('v3/projects:share.image.downloading')}
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {t('v3/projects:share.image.downloadButton')}
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {/* 图片状态：错误 */}
                    {imageStatus === 'error' && (
                      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-ios-lg">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-red-500 dark:text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-ios-subhead text-red-600 dark:text-red-400 font-medium">
                            {t('v3/projects:share.image.generationFailed')}
                          </p>
                        </div>
                        {imageError && (
                          <p className="text-ios-footnote text-red-500 dark:text-red-400 mt-1">
                            {imageError}
                          </p>
                        )}
                      </div>
                    )}

                    {/* 图片状态：未开始 */}
                    {imageStatus === 'not-started' && (
                      <div className="p-4 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-ios-lg text-center">
                        <svg className="w-8 h-8 text-ios-gray dark:text-ios-dark-gray mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                          {t('v3/projects:share.image.autoGenerate', '分享项目后将自动生成预览图片')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 加载指示器 */}
              {loading && (
                <div className="flex justify-center items-center mt-4">
                  <div className="w-5 h-5 border-2 border-ios-blue/30 dark:border-ios-dark-blue/30 border-t-ios-blue dark:border-t-ios-dark-blue rounded-full animate-spin"></div>
                  <span className="ml-2 text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
                    {t('common:loadingText')}...
                  </span>
                </div>
              )}
            </div>

            {/* 模态框按钮 */}
            <div className="border-t border-ios-gray5 dark:border-ios-dark-gray4">
              <button
                onClick={onClose}
                className="w-full py-3 text-ios-body text-ios-blue dark:text-ios-dark-blue font-medium"
              >
                {t('common:actions.close')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
